/* eslint-disable max-len */

import { UIMatch, generatePath } from 'react-router-dom'

import { CsRoute } from '../types'
import { DynamicBreadcrumb } from '../breadcrumbs'
import { Link } from '@mui/material'
import { prepareRoutes } from '../utils/prepareRoutes'
import { routePaths } from '../route-paths'
import { useOrgBenefitPlanRoutes } from './benefit-plans/useBenefitPlanRoutes'
import { useOrgOpenEnrollmentRoutes } from './open-enrollments/useOpenEnrollmentRoutes'
import { useOrgProviderRoutes } from 'routes/employer-provider-routes'
import { useOrganizationFileTransferRoutes } from 'routes/organization-file-transfer-routes'
import { usePayGroupRoutes } from './pay-groups/usePayGroupRoutes'

export const useOrgRoutes = (): CsRoute[] => {
	return prepareRoutes(
		[
			{
				path: routePaths.organizations.root,
				handle: {
					crumb: () => <Link href={routePaths.organizations.root}>Organizations</Link>,
				},
				children: prepareRoutes(
					[
						{
							// this is the index of admin's entire website
							index: true,
							lazy: () => import('pages/organizations').then((module) => ({ Component: module.default })),
						},
						{
							lazy: () => import('pages/create-org').then((module) => ({ Component: module.default })),
							path: routePaths.organizations.create.path,
						},
						{
							lazy: () => import('pages/edit-org').then((module) => ({ Component: module.EditOrgLayout })),
							path: `${routePaths.organizations.edit.root}/*`,
							handle: {
								crumb: (data: UIMatch<unknown, unknown>) => {
									return <DynamicBreadcrumb portalId={data.params.id ?? ''} />
								},
							},
							children: prepareRoutes(
								[
									{
										path: routePaths.organizations.edit.faq.path,
										lazy: () =>
											import('pages/faqs').then((module) => ({
												Component: module.default,
											})),
									},
									{
										path: routePaths.organizations.edit.flags.path,
										lazy: () =>
											import('pages/organization-flags').then((module) => ({
												Component: module.default,
											})),
									},
									useOrgBenefitPlanRoutes(),
									useOrgOpenEnrollmentRoutes(),
									usePayGroupRoutes(),
									{
										path: routePaths.organizations.edit.employeeConsent.path,
										lazy: () =>
											import('pages/edit-org/employee-consent').then((module) => ({
												Component: module.default,
											})),
									},
									{
										path: routePaths.organizations.edit.fileTransfer.path,
										handle: {
											crumb: (data: UIMatch<any, any>) => (
												<Link
													href={generatePath(routePaths.organizations.edit.fileTransfer.path, {
														id: data.params.id ?? '',
													})}
												>
													File Transfer & Encryption
												</Link>
											),
										},
										children: [
											{
												index: true,
												lazy: () =>
													import('pages/organization-file-transfer').then((module) => ({ Component: module.default })),
											},
											{
												lazy: () =>
													import('pages/organization-file-transfer-layout').then((module) => ({
														Component: module.default,
													})),
												children: useOrganizationFileTransferRoutes(),
											},
										],
									},
									{
										path: routePaths.organizations.edit.providers.path,
										handle: {
											crumb: (data: UIMatch<any, any>) => (
												<Link
													href={generatePath(routePaths.organizations.edit.providers.path, {
														id: data.params.id ?? '',
													})}
												>
													Providers
												</Link>
											),
										},
										children: [
											{
												index: true,
												lazy: () =>
													import('pages/organization-providers').then((module) => ({ Component: module.default })),
											},
											{
												path: ':providerId/*',
												lazy: () => import('pages/employer-provider').then((module) => ({ Component: module.default })),
												children: useOrgProviderRoutes(),
												handle: {
													crumb: (data: UIMatch<any, any>) => {
														return <DynamicBreadcrumb portalId={data.params.providerId ?? ''} />
													},
												},
											},
										],
									},
									{
										path: routePaths.organizations.edit.financials.path,
										lazy: () =>
											import('pages/reconciliation-configuration').then((module) => ({
												Component: module.ReconciliationConfiguration,
											})),
									},
									{
										path: routePaths.organizations.edit.qleEvents.path,
										lazy: () =>
											import('pages/edit-org/qle-events').then((module) => ({
												Component: module.default,
											})),
									},
								],
								{
									stripLeading: `${routePaths.organizations.edit.root}/`,
								},
							),
						},
					],
					{
						stripLeading: `${routePaths.organizations.root}/`,
					},
				),
			},
		],
		{ stripLeading: `${routePaths.organizations.root}/` },
	)
}
